import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import MotionReveal from "@/components/MotionReveal";
import api, { API_BASE } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X, ChevronLeft, ChevronRight, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const toAbs = (url) => (url?.startsWith("http") ? url : `${BACKEND_URL}${url}`);

const Gallery = () => {
  const { t, lang } = useLang();
  const { token, adminToken } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState(-1);
  const [downloading, setDownloading] = useState(false);
  const [view, setView] = useState("grid"); // "grid" | "timeline"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/photos");
      setItems(res.data || []);
    } catch (_e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const open = openIdx >= 0 && openIdx < items.length;
  const current = open ? items[openIdx] : null;

  const onKey = useCallback(
    (e) => {
      if (!open) return;
      if (e.key === "Escape") setOpenIdx(-1);
      if (e.key === "ArrowRight") setOpenIdx((i) => (i + 1) % items.length);
      if (e.key === "ArrowLeft") setOpenIdx((i) => (i - 1 + items.length) % items.length);
    },
    [open, items.length]
  );

  useEffect(() => {
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onKey]);

  const grid = useMemo(
    () => (
      <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 sm:gap-5 [column-fill:_balance]">
        {items.map((it, i) => (
          <button
            type="button"
            key={it.id}
            onClick={() => setOpenIdx(i)}
            data-testid={`gallery-image-open-${i}`}
            className="group relative block w-full mb-4 sm:mb-5 break-inside-avoid overflow-hidden rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <img
              src={toAbs(it.url)}
              alt={it.original_name || "Guest photo"}
              loading="lazy"
              className="block w-full h-auto transition-transform duration-700 group-hover:scale-[1.02] shadow-[0_10px_30px_rgba(31,31,31,0.08)]"
            />
            <span className="pointer-events-none absolute inset-0 bg-foreground/0 group-hover:bg-foreground/5 transition-colors" />
          </button>
        ))}
      </div>
    ),
    [items]
  );

  // Timeline: flat chronological (oldest → newest) sorted by EXIF taken_at
  // when available; falls back to upload time so nothing is dropped.
  const timelineItems = useMemo(() => {
    const withTs = items.map((it, origIdx) => ({
      ...it,
      _origIdx: origIdx,
      _ts: it.taken_at || it.uploaded_at,
      _isReal: Boolean(it.taken_at),
    }));
    withTs.sort((a, b) => new Date(a._ts).getTime() - new Date(b._ts).getTime());
    return withTs;
  }, [items]);

  const dateFmtLong = useMemo(
    () =>
      new Intl.DateTimeFormat(
        lang === "el" ? "el-GR" : lang === "fr" ? "fr-FR" : lang === "pl" ? "pl-PL" : "en-GB",
        { weekday: "long", day: "numeric", month: "long", year: "numeric" }
      ),
    [lang]
  );
  const timeFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(
        lang === "el" ? "el-GR" : lang === "fr" ? "fr-FR" : lang === "pl" ? "pl-PL" : "en-GB",
        { hour: "2-digit", minute: "2-digit" }
      ),
    [lang]
  );

  const timeline = useMemo(
    () => (
      <div className="relative pl-6 sm:pl-10" data-testid="gallery-timeline">
        <span
          aria-hidden
          className="absolute left-[7px] sm:left-[15px] top-0 bottom-0 w-px bg-border"
        />
        <ol className="space-y-6 sm:space-y-8">
          {timelineItems.map((it) => {
            const d = new Date(it._ts);
            const isReal = it._isReal;
            return (
              <li key={it.id} className="relative" data-testid={`gallery-timeline-item-${it._origIdx}`}>
                <span
                  aria-hidden
                  className={
                    "absolute -left-[26px] sm:-left-[38px] top-2 w-3 h-3 rounded-full border-2 border-background " +
                    (isReal ? "bg-accent" : "bg-foreground/30")
                  }
                />
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="font-serif-display text-xl sm:text-2xl tabular-nums">
                    {timeFmt.format(d)}
                  </span>
                  <span className="editorial-kicker text-foreground/50 text-[10px]">
                    {dateFmtLong.format(d)}
                  </span>
                  {!isReal && (
                    <span
                      className="editorial-kicker text-[9px] text-foreground/40"
                      title={t("gallery.timelineNoExifTooltip") || "No original timestamp — using upload time"}
                    >
                      · {t("gallery.timelineFallback") || "uploaded"}
                    </span>
                  )}
                  {it.uploader && (
                    <span className="text-xs text-foreground/50 italic font-serif-display">
                      · {it.uploader}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setOpenIdx(it._origIdx)}
                  className="mt-3 block w-full overflow-hidden rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-accent group"
                  data-testid={`gallery-timeline-open-${it._origIdx}`}
                >
                  <img
                    src={toAbs(it.url)}
                    alt={it.original_name || "Guest photo"}
                    loading="lazy"
                    className="block w-full h-auto max-h-[80vh] object-cover transition-transform duration-700 group-hover:scale-[1.01] shadow-[0_10px_30px_rgba(31,31,31,0.08)]"
                  />
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    ),
    [timelineItems, timeFmt, dateFmtLong, t]
  );

  const onDownloadAll = useCallback(async () => {
    if (downloading || items.length === 0) return;
    setDownloading(true);
    try {
      const bearer = adminToken || token;
      const res = await fetch(`${API_BASE}/photos/download-all.zip`, {
        headers: bearer ? { Authorization: `Bearer ${bearer}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      // Try to reuse the server-suggested filename
      let filename = "eli-mag-wedding-photos.zip";
      const disp = res.headers.get("Content-Disposition") || "";
      const match = disp.match(/filename="?([^";]+)"?/i);
      if (match) filename = match[1];
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Give the browser a beat before revoking the blob URL
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (_e) {
      toast.error("Download failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  }, [downloading, items.length, token, adminToken]);

  return (
    <div data-testid="gallery-page">
      <section className="mx-auto max-w-[1180px] px-5 sm:px-8 pt-24 sm:pt-32">
        <MotionReveal>
          <p className="editorial-kicker">{t("gallery.overline")}</p>
        </MotionReveal>
        <MotionReveal delay={0.05}>
          <h1 className="editorial-h1 mt-6 text-4xl sm:text-6xl lg:text-7xl max-w-[20ch]">
            {t("gallery.title")}
          </h1>
        </MotionReveal>
        <MotionReveal delay={0.1}>
          <div className="mt-8 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <p className="text-foreground/75 max-w-[52ch] leading-relaxed">{t("gallery.intro")}</p>
            {items.length > 0 && (
              <button
                type="button"
                onClick={onDownloadAll}
                disabled={downloading}
                data-testid="gallery-download-all"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-foreground text-background text-[11px] tracking-[0.22em] uppercase hover:bg-foreground/85 disabled:opacity-60 disabled:cursor-not-allowed transition-colors shrink-0"
              >
                {downloading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>{t("gallery.preparing")}</span>
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    <span>{t("gallery.downloadAll")}</span>
                  </>
                )}
              </button>
            )}
          </div>
        </MotionReveal>
      </section>

      <section className="mx-auto max-w-[1180px] px-5 sm:px-8 mt-12 sm:mt-16 pb-20">
        {loading ? (
          <p className="editorial-kicker animate-pulse" data-testid="page-loading">· · ·</p>
        ) : items.length === 0 ? (
          <div className="surface-card p-10 text-center" data-testid="gallery-empty">
            <p className="editorial-kicker">{t("gallery.overline")}</p>
            <p className="font-serif-display italic text-2xl sm:text-3xl mt-4 max-w-[44ch] mx-auto">
              {t("gallery.empty")}
            </p>
          </div>
        ) : (
          <>
            <div
              className="mb-8 sm:mb-10 inline-flex items-center gap-1 rounded-full border border-border p-1 bg-background/50"
              role="tablist"
              data-testid="gallery-view-toggle"
            >
              {[
                { key: "grid", label: t("gallery.viewGrid") || "Grid" },
                { key: "timeline", label: t("gallery.viewTimeline") || "Timeline" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  role="tab"
                  aria-selected={view === opt.key}
                  onClick={() => setView(opt.key)}
                  data-testid={`gallery-view-${opt.key}`}
                  className={
                    "h-8 px-4 rounded-full text-[11px] tracking-[0.22em] uppercase transition-colors " +
                    (view === opt.key
                      ? "bg-foreground text-background"
                      : "text-foreground/55 hover:text-foreground")
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {view === "grid" ? grid : timeline}
          </>
        )}
      </section>

      <Dialog open={open} onOpenChange={(o) => !o && setOpenIdx(-1)}>
        <DialogContent
          data-testid="gallery-lightbox-dialog"
          className="max-w-[96vw] sm:max-w-[1200px] p-0 bg-background/95 backdrop-blur-md border-border"
        >
          {current && (
            <div className="relative">
              <img
                src={toAbs(current.url)}
                alt={current.original_name || "Photo"}
                className="w-full max-h-[82vh] object-contain"
              />
              <div className="absolute top-3 right-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => setOpenIdx(-1)}
                  data-testid="gallery-lightbox-close"
                  aria-label={t("gallery.close")}
                  className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-background border border-border"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              {items.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setOpenIdx((i) => (i - 1 + items.length) % items.length)}
                    data-testid="gallery-lightbox-prev"
                    aria-label={t("gallery.prev")}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 inline-flex items-center justify-center rounded-full bg-background/80 border border-border hover:bg-background"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpenIdx((i) => (i + 1) % items.length)}
                    data-testid="gallery-lightbox-next"
                    aria-label={t("gallery.next")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 inline-flex items-center justify-center rounded-full bg-background/80 border border-border hover:bg-background"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
              <div className="px-5 py-3 editorial-kicker text-center border-t border-border">
                {(current.uploader && current.uploader.trim()) || "Eli & Mag"} · {openIdx + 1} / {items.length}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Gallery;
